/**
 * RequestNewInvitePage Component
 * Public page for requesting new invitation link
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { usersService } from '../services/users.service';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Turnstile } from '../components/ui/Turnstile';
import { useToast } from '../hooks/useToast';
import { useTurnstile } from '../hooks/useTurnstile';

export const RequestNewInvitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const tokenFromUrl = searchParams.get('token');
  const { turnstileToken, onVerify, resetTurnstile, isTurnstileReady } = useTurnstile();

  const [email, setEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const requestNewMutation = useMutation({
    mutationFn: (data: { token?: string; email?: string; turnstile_token?: string }) =>
      usersService.requestNewInvitation(data),
    onSuccess: () => {
      setIsSuccess(true);
      toast.success('New invitation sent! Please check your email.');
    },
    onError: (error: unknown) => {
      resetTurnstile();
      const message = error instanceof Error ? error.message : 'Failed to request new invitation';
      toast.error(message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (tokenFromUrl) {
      requestNewMutation.mutate({ token: tokenFromUrl, turnstile_token: turnstileToken });
    } else if (email) {
      requestNewMutation.mutate({ email, turnstile_token: turnstileToken });
    } else {
      toast.error('Please provide your email address');
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-app">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <img
              src="/team-wizard-logo-small.png"
              alt="Team Wizard"
              className="h-12 w-auto mx-auto"
            />
          </div>
          <div className="text-green-500 text-5xl mb-4">✉️</div>
          <h1 className="text-2xl font-bold text-heading mb-2">Email Sent!</h1>
          <p className="text-meta mb-6">
            We've sent you a new invitation email. Please check your inbox and follow the link to create your account.
          </p>
          <p className="text-sm text-meta mb-6">
            The link will expire in 7 days.
          </p>
          <Button onClick={() => navigate('/login')} variant="secondary">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-app">
      <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="mb-6">
            <img
              src="/team-wizard-logo-small.png"
              alt="Team Wizard"
              className="h-12 w-auto mx-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-heading mb-2">
            Request New Invitation
          </h1>
          <p className="text-meta">
            Enter your email to receive a new invitation link
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!tokenFromUrl && (
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              helperText="Enter the email address where you received the original invitation"
            />
          )}

          {tokenFromUrl && (
            <div className="bg-status-info-subtle border border-status-info rounded-lg p-4">
              <p className="text-sm text-body">
                We'll send a new invitation to the email address associated with your original invitation.
              </p>
            </div>
          )}

          <Turnstile onVerify={onVerify} action="request-invitation" />

          <Button
            type="submit"
            isLoading={requestNewMutation.isPending}
            disabled={!isTurnstileReady}
            className="w-full"
          >
            Request New Invitation
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-sm text-meta hover:text-heading"
          >
            Back to Login
          </button>
        </div>

        <div className="mt-8 border-t border-[var(--border-default)] pt-6">
          <p className="text-xs text-meta text-center">
            Note: You can request up to 3 new invitations per hour. If you continue to experience issues, please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};
